// Platform-admin model pin for one run. Hidden for everyone else: the
// viewer gate is the same `/users/me` admin flag the server enforces on
// the model-options endpoint and the pin itself.
import { type ReactNode, useRef, useState } from 'react';

import { AgentModelPicker } from '@apps/web/components/agent_model_picker';
import { updateRunModel } from '@apps/web/features/phoebe_chat_v3/api/run_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { toast } from '@apps/web/lib/app_notifications';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

const REASON = 'v3-agent-run-model-picker';

export function RunModelPickerControl({
    scope,
    initialActiveModel,
    initialResolvedModel,
    disabled = false,
}: Readonly<{
    readonly scope: HarnessScope;
    readonly initialActiveModel: string | null;
    readonly initialResolvedModel: string | null;
    readonly disabled?: boolean;
}>): ReactNode {
    const viewer = useApiQuery({
        path: '/users/me',
        method: 'get',
        reason: REASON,
        select: (response) => response.user,
    });
    const isAdmin = viewer.data?.admin === true;
    const options = useApiQuery({
        path: '/admin/agent/model-options',
        method: 'get',
        enabled: isAdmin,
        reason: REASON,
        select: (response) => response.options,
    });
    const [activeModel, setActiveModel] = useState(initialActiveModel);
    const [resolvedModel, setResolvedModel] = useState(initialResolvedModel);
    const [saving, setSaving] = useState(false);
    const confirmedRef = useRef({
        active: initialActiveModel,
        resolved: initialResolvedModel,
    });
    if (!isAdmin) {
        return null;
    }
    function selectModel(model: string | null): void {
        setSaving(true);
        setActiveModel(model);
        void updateRunModel(scope, model)
            .then((response) => {
                confirmedRef.current = {
                    active: response.active_model ?? null,
                    resolved: response.resolved_model ?? null,
                };
                setActiveModel(confirmedRef.current.active);
                setResolvedModel(confirmedRef.current.resolved);
            })
            .catch(() => {
                setActiveModel(confirmedRef.current.active);
                setResolvedModel(confirmedRef.current.resolved);
                toast.error('Phoebe could not change the model.', {
                    persistNotification: false,
                });
            })
            .finally(() => {
                setSaving(false);
            });
    }
    return (
        <AgentModelPicker
            pinnedModel={activeModel}
            resolvedModel={resolvedModel}
            options={options.data}
            disabled={disabled || saving}
            onModelChange={selectModel}
            variant="pill"
        />
    );
}
